import type { LucideIcon } from "lucide-react";
import {
  Activity,
  CalendarDays,
  CheckSquare,
  FileText,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  StickyNote,
} from "lucide-react";
import { Tooltip } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";

// Layout Standard §3 / §6 — entity detail pages render the canonical
// 9-tab workspace as a sticky tab bar. By default every tab is icon-only
// at every viewport width; the active tab is distinguished by the gold
// underline + onyx text-color accent. Hovering OR keyboard-focusing a tab
// reveals the full label via the shared <Tooltip>; aria-label keeps the full
// label reachable to screen readers. Tab badges render alongside the icon as
// compact "(N)" suffixes. Layout Standard §3 canonical order is preserved
// — consumers pass items in order.
//
// The reveal is the shared <Tooltip>, which PORTALS its bubble to <body> and
// contributes no DOM node of its own. That is what makes it safe inside the
// §6.8 horizontal scroller below: an absolutely-positioned span would be
// clipped by the scroll container and would give the strip permanent vertical
// scrollable overflow, but a portaled bubble has no ancestor that can clip it
// and adds nothing to the strip's box. It also opens on `:focus-visible`,
// which a native `title` never did — so a sighted keyboard user tabbing
// through 19 icon-only tabs is finally told what each one is.
//
// showLabels (Layout Standard v1.1) — opts a page into visible text
// labels alongside the icons. A per-item `shortLabel` keeps long strips
// scannable ("Payments" for "Payment Tracking"); aria-label and the hover
// tooltip always carry the FULL label, and the tooltip is suppressed when
// it would just repeat the visible text. Pages that keep the icon-only
// default (e.g. Deal detail, which overflowed with labels at 1536–1920px)
// are unaffected — and that default is what almost every page wants: only
// 2 of 21 detail pages opt in, and Loan tried labels in PR #609 and
// reverted in #611 when 24 of them wrapped.
export type EntityTabItem<TKey extends string = string> = {
  key: TKey;
  label: string;
  /** Shorter display text used only when the bar renders visible labels. */
  shortLabel?: string;
  icon: LucideIcon;
  badge?: string | number | null;
  /**
   * Severity tint for entity-tab count badges (Layout Standard §5 —
   * worst-severity amber/red). Omitted = neutral gray volume count.
   */
  badgeTone?: "warning" | "error";
};

export type EntityTabBarProps<TKey extends string = string> = {
  ariaLabel: string;
  testIdPrefix: string;
  items: ReadonlyArray<EntityTabItem<TKey>>;
  value: TKey;
  onChange: (key: TKey) => void;
  /** Render visible text labels alongside the icons (default icon-only). */
  showLabels?: boolean;
  className?: string;
};

// Cross-cutting tab icons (Layout Standard §3 standardized block).
// Communications uses MessageCircle to mirror the Comm Log nav icon;
// Messages uses MessageSquare to mirror Internal Messaging; Calendar uses
// CalendarDays to mirror the Calendar nav icon (SidebarMenu.cs).
export const CROSS_CUTTING_TAB_ICONS = {
  overview: LayoutDashboard,
  documents: FileText,
  tasks: CheckSquare,
  calendar: CalendarDays,
  messages: MessageSquare,
  communications: MessageCircle,
  notes: StickyNote,
  activity: Activity,
} as const satisfies Record<string, LucideIcon>;

export function EntityTabBar<TKey extends string>({
  ariaLabel,
  testIdPrefix,
  items,
  value,
  onChange,
  showLabels = false,
  className,
}: EntityTabBarProps<TKey>) {
  return (
    <nav
      className={cn(
        "sticky top-0 z-10 -mx-space-4 px-space-4 bg-white-pure border-b border-border-light",
        className,
      )}
      aria-label={ariaLabel}
      data-testid={`${testIdPrefix}-tab-bar`}
    >
      {/*
        Layout Standard §6.8 — an overflowing strip SCROLLS horizontally; it
        must not reflow onto a second row. The scroll container is this inner
        div rather than the <nav>, because an ancestor scroll container breaks
        `position: sticky` on its descendants and the nav is the sticky element.

        `pb-px -mb-px` absorbs the 1px each button pulls below the flex line
        (its own `-mb-px`, which overlaps the active gold underline onto the
        nav's border). Without the padding that 1px is vertical overflow, and
        overflow-y is `auto` here whether we ask for it or not — so the strip
        would grow a scrollbar over a single pixel. The negative margin hands
        the pixel straight back, leaving the nav exactly as tall as before.
      */}
      <div className="flex gap-space-1 overflow-x-auto pb-px -mb-px">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.key === value;
          const hasBadge = item.badge !== null && item.badge !== undefined;
          const visibleLabel = showLabels ? (item.shortLabel ?? item.label) : null;
          // Suppressed when it would only repeat text already on screen.
          const showTooltip = visibleLabel !== item.label;
          const button = (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={item.label}
              data-testid={`${testIdPrefix}-tab-${item.key}`}
              onClick={() => onChange(item.key)}
              className={cn(
                // shrink-0 keeps a tab at its natural width inside the
                // scroller — a flex item would otherwise compress rather than
                // overflow, and nothing would ever scroll.
                "shrink-0 px-space-3 py-space-2 text-small font-display uppercase tracking-wide border-b-2 -mb-px inline-flex items-center gap-space-2",
                active
                  ? "border-gold text-onyx"
                  : "border-transparent text-gray-mid hover:text-onyx",
              )}
            >
              <Icon size={16} strokeWidth={1.5} aria-hidden />
              {visibleLabel !== null && (
                <span className="whitespace-nowrap">{visibleLabel}</span>
              )}
              {hasBadge && (
                <span
                  className={cn(
                    "text-label normal-case",
                    item.badgeTone === "error"
                      ? "text-status-error"
                      : item.badgeTone === "warning"
                        ? "text-status-warning"
                        : "text-gray-mid",
                  )}
                >
                  ({item.badge})
                </span>
              )}
            </button>
          );
          // Wrapped only when the tooltip would say something the tab does not
          // already show. <Tooltip> renders a Fragment plus a portal, so the
          // <button> stays a DIRECT flex child of the scroller either way.
          return showTooltip ? (
            <Tooltip key={item.key} label={item.label}>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </div>
    </nav>
  );
}
